import React from "react";
import { useTranslation } from "react-i18next";
import { DataTable } from "@/components/DataTable";
import { EmptyState } from "@/components/DataTable/States/Empty";
import { CloudUpload } from "lucide-react";
import { DataManagementFile } from "@/hooks/useDataManagementFiles";
import { useDataManagementColumns } from "./useDataManagementColumns";

interface DataManagementTableProps {
  files: DataManagementFile[];
  loading: boolean;
  totalCount: number;
  pageIndex: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  onNextPage: () => void;
  onPreviousPage: () => void;
  onDownload: (file: DataManagementFile) => void;
}

export const DataManagementTable = ({
  files,
  loading,
  totalCount,
  pageIndex,
  hasNextPage,
  hasPreviousPage,
  onNextPage,
  onPreviousPage,
  onDownload,
}: DataManagementTableProps) => {
  const { t } = useTranslation();
  const columns = useDataManagementColumns({ onDownload });

  const DEFAULT_ROWS = 10;
  const getPageCount = (total: number) => {
    return Math.ceil(total / DEFAULT_ROWS);
  };

  return (
    <section className="p-6">
      <div className="rounded-lg border shadow-sm">
        <DataTable
          data={files}
          columns={columns}
          isLoading={loading}
          topHeader={
            <DataTable.Title
              title={t("dataManagement.table.title")}
              description={t("dataManagement.table.subtitle")}
            />
          }
          tableHeader={<DataTable.THeader />}
          pagination={
            <>
              <DataTable.PaginationLabel
                pageCount={getPageCount(totalCount)}
                pageIndex={pageIndex}
              />
              <DataTable.PaginationControls
                onNextPage={hasNextPage ? onNextPage : false}
                onPreviousPage={hasPreviousPage ? onPreviousPage : false}
              />
            </>
          }
          tableBody={<DataTable.TBody />}
          emptyState={
            <EmptyState
              copy={t("dataManagement.table.empty")}
              icon={
                <CloudUpload
                  className="h-[36px] w-[36px] text-primary-light"
                  aria-label={t("dataManagement.upload.button")}
                />
              }
            />
          }
        />
      </div>
    </section>
  );
};
